import React from 'react'
import { ArrowUpDown, List, Grid, X } from 'lucide-react'

import { SkeletonLoadingHistory } from 'src/features/SkeletonLoadingHistory'
import { SheetForHistoryOfFuelTracking } from 'src/features/SheetForHistoryOfFuelTracking'
import { FuelHistoryCard } from 'src/features/FuelHistoryCard'
import { ButtonNavigateToCars } from 'src/features/ButtonNavigateToCars'
import { TooltipWrapper } from 'src/components/Tooltip'
import Title from 'src/components/Title'
import Container from 'src/components/Container'
import AppBreadcrumb from 'src/components/AppBreadcrumb'

import { useFuelTrackingHistory } from 'src/pages/FuelTrackingHistory/useFuelTrackingHistory'
import {
  Select,
  SelectItem,
  SelectGroup,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'src/components/shadcn/pagination'
import { Input } from 'src/components/shadcn/input'
import { Button } from 'src/components/shadcn/button'
import { Badge } from 'src/components/shadcn/badge'

const FuelTrackingHistory = (): JSX.Element => {
  const {
    locale,
    breadcrumbItems,
    searchText,
    handleSearchChange,
    allFuelHistory,
    isLoadingGetAllFuelHistory,
    sortState,
    handleChangeSortState,
    listOfSorts,
    isReversed,
    isList,
    toggleListDisplay,
    toggleReversed,
    arrayPages,
    handlePreviousPage,
    handleNextPage,
    handleSetPage,
    currentPage,
    clearInput,
    filters,
    setFilters,
    filtersCount,
    inputRef,
    currentCar,
    numberOfFoundItems,
    hiddenOnMobile,
  } = useFuelTrackingHistory()

  return (
    <Container>
      <AppBreadcrumb items={breadcrumbItems} />
      <div className="flex justify-between gap-3 mt-5">
        <div className="relative w-full">
          <Input
            ref={inputRef}
            placeholder={locale.inputPlaceholder}
            className="rounded-[10px]"
            value={searchText}
            onChange={handleSearchChange}
          />
          {searchText.length > 0 && (
            <div className="absolute top-[5px] right-1 flex flex-row gap-1 items-center">
              <Badge className="rounded-[15px] select-none h-5 w-5 text-[12px] flex items-center justify-center">
                {numberOfFoundItems}
              </Badge>
              <Button
                variant="ghost"
                className="rounded-[15px] w-8 h-8"
                onClick={clearInput}
              >
                <X />
              </Button>
            </div>
          )}
        </div>
        <Select value={sortState} onValueChange={handleChangeSortState}>
          <SelectTrigger className="lg:w-1/4 sm:w-1/2 w-full rounded-[10px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {listOfSorts.map(({ type, title }) => (
                <SelectItem value={type}>{title}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div className="mt-5 flex justify-between">
        <div className="flex items-center">
          <h2 className={`${hiddenOnMobile} font-bold`}>{currentCar}:</h2>
          <ButtonNavigateToCars />
        </div>
        <div className="flex gap-3">
          <TooltipWrapper text={locale.tooltips.list}>
            <Button
              variant="outline"
              onClick={toggleListDisplay}
              className="rounded-[10px]"
            >
              {isList ? <List /> : <Grid />}
            </Button>
          </TooltipWrapper>
          <TooltipWrapper text={locale.tooltips.reverse}>
            <Button
              variant="outline"
              onClick={toggleReversed}
              className={`rounded-[10px] ${isReversed ? 'bg-accent' : ''}`}
            >
              <ArrowUpDown />
            </Button>
          </TooltipWrapper>
          <SheetForHistoryOfFuelTracking
            filters={filters}
            setFilters={setFilters}
            filtersCount={filtersCount}
          />
        </div>
      </div>
      <div className="mt-5">
        {isLoadingGetAllFuelHistory ? (
          <>
            <SkeletonLoadingHistory isList={isList} countOfFields={5} />
          </>
        ) : allFuelHistory.length > 0 ? (
          <div>
            <div
              className={
                isList ? '' : 'grid md:grid-cols-2 lg:grid-cols-3 gap-4'
              }
            >
              {allFuelHistory.map((fuel, index) => (
                <div key={index} className={isList ? 'mt-3' : ''}>
                  <FuelHistoryCard {...fuel} />
                </div>
              ))}
            </div>
            <Pagination className="my-5">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={handlePreviousPage}
                    className="rounded-[10px] select-none"
                  />
                </PaginationItem>
                {arrayPages.map(page => {
                  return (
                    <PaginationItem key={crypto.randomUUID()}>
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => handleSetPage(page)}
                        className="rounded-[10px] select-none"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={handleNextPage}
                    className="rounded-[10px] select-none"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        ) : (
          <Title
            title={locale.noResult}
            classes="text-center text-xl font-bold mt-8"
          />
        )}
      </div>
    </Container>
  )
}

export default FuelTrackingHistory
